// @flow
import actionCreators from "@modules/reporting/actionCreator";
import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import {
  GetSlaPerformanceTrendsDefaultReports,
  GetSlaPerformanceTrendsMyReports,
  UpdateSlaPerformanceTrendsReport,
} from "@modules/reporting/constants/actions";
import http from "@http";
import type { SlaPerformanceReport } from "@modules/reporting/reducer/trends/sla-performance/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type TrendsReportsRequest = $Exact<{
  clientId: string,
}>;

export function* onGetSlaPerformanceTrendsDefaultReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<SlaPerformanceReport> }> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/sla/performance/default-reports`,
    );

    yield put(actionCreators.GetSlaPerformanceTrendsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSlaPerformanceTrendsDefaultReportsError(e.response));
  }
}

export function* onGetSlaPerformanceTrendsMyReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<SlaPerformanceReport> }> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/sla/performance/my-saved-reports`,
    );

    yield put(actionCreators.GetSlaPerformanceTrendsMyReportsSuccess(response.data));
    yield put(actionCreators.ReSetSlaPerformanceTrendsReportToSave());
  } catch (e) {
    yield put(actionCreators.GetSlaPerformanceTrendsMyReportsError(e.response));
  }
}

type TrendsReportDataRequest = $Exact<{
  ...TrendsReportsRequest,
  report: SlaPerformanceReport,
  canSave?: boolean,
}>;

export function* onUpdateSlaPerformanceTrendsReport({
  payload,
}: Action<TrendsReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/trends/client/${payload.clientId}/sla/performance/my-saved-reports/${payload.report.id}`,
      payload.report,
    );

    yield all([
      put(
        actionCreators.GetSlaPerformanceTrendsReportData({
          clientId: payload.clientId,
          report: {
            ...payload.report,
            filters: response.data.filters,
          },
          canSave: false,
        }),
      ),
      put(sendSuccessMessage("REPORT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateSlaPerformanceTrendsReportError(e.response));
  }
}

export function* SlaPerformanceSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetSlaPerformanceTrendsDefaultReports, onGetSlaPerformanceTrendsDefaultReports),
    takeEvery(GetSlaPerformanceTrendsMyReports, onGetSlaPerformanceTrendsMyReports),
    takeEvery(UpdateSlaPerformanceTrendsReport, onUpdateSlaPerformanceTrendsReport),
  ]);
}

export default SlaPerformanceSaga;
